import { useEffect, useMemo, useState } from 'react'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { Box, useMediaQuery } from '@mui/material'
import { Breadcrumb } from '@components/shared/Breadcrumb'
import { CardWrapper } from '@components/shared/CardWrapper'
import { emotionTheme } from '@styles/theme'
import { FormProvider, useForm, type FieldErrors } from 'react-hook-form'
import { getPagePermissions } from '@hooks/useUser/provider'
import { getProfileType } from '@pages/AccessProfile/helpers/getProfileType'
import { initialFormValues } from './form/initialFormValues'
import { Input } from '@components/shared/Input'
import { LoadingGrid } from '@components/shared/LoadingGrid'
import { PageIds } from '@hooks/useLayout/types'
import { PermissionSettings } from '../PermissionSettings'
import { RowBox } from '@components/shared/RowBox'
import { Select } from '@components/shared/Select'
import { Switch } from '@components/shared/Switch'
import { Textarea } from '@components/shared/Textarea'
import { Title } from '@components/shared/Title'
import { useAccessProfile } from '@pages/AccessProfile/context'
import { useUser } from '@hooks/useUser'

import type { AccessProfileModalProps } from './types'
import type { AccessProfileModalFormProps } from './form/types'
import type { PermissionsItemProps } from '@pages/AccessProfile/context/types'

export const AccessProfileModal = ({ data, open, onClose, ...rest }: AccessProfileModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const {
    profilesData,
    isProfilesLoading,
    isAccessProfileByIdLoading,
    getAccessProfileById,
    savePermissions,
    isSavePermissionsLoading
  } = useAccessProfile()

  const { userPermissionsData } = useUser()

  const isSuperAdmin = userPermissionsData?.isSuperAdmin || userPermissionsData?.isAdmin

  const [permissions, setPermissions] = useState<PermissionsItemProps | null>(null)

  const methods = useForm<AccessProfileModalFormProps>({
    defaultValues: initialFormValues
  })

  const profileTypeOptions = useMemo(() => {
    return (
      profilesData?.availableFilters?.profileType
        .map((profile) => ({
          label: getProfileType(profile),
          value: profile
        }))
        .filter((item) => item.value !== 'Todos') || []
    )
  }, [profilesData])

  const statusOptions = useMemo(() => {
    return (
      profilesData?.availableFilters?.status
        .map((status) => ({ label: status, value: status }))
        .filter((item) => item.value !== 'Todos') || []
    )
  }, [profilesData?.availableFilters?.status])

  const onError = (errors: FieldErrors<AccessProfileModalFormProps>) => console.log({ errors })

  const onSubmit = (formData: AccessProfileModalFormProps) => {
    formData.profileId = data?.id!

    savePermissions(formData)
  }

  useEffect(() => {
    const pagePermissions = getPagePermissions(PageIds.ACCESS_PROFILE)
    setPermissions(pagePermissions)
  }, [userPermissionsData])

  const handleInitialActionsFromScreen = () => {
    getAccessProfileById(data?.id?.toString() || '')
  }

  useEffect(() => {
    if (!!data?.id && open) {
      handleInitialActionsFromScreen()
    }
  }, [data, open])

  return (
    <BaseModal2
      open={open}
      width={isMobile ? 'calc(100% - 20px)' : '96.5%'}
      heightContainer={isMobile ? 'calc(100% - 20px)' : '95%'}
      headerIcon={<Breadcrumb breadcrumbData={[{ label: 'Perfil de Acesso' }, { label: data?.name || '' }]} />}
      onClose={onClose}
      footer={{
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Salvar',
        onConfirm: () => methods.handleSubmit(onSubmit, onError)(),
        confirmButtonDisabled: isAccessProfileByIdLoading || !permissions?.canEdit,
        loading: isSavePermissionsLoading
      }}
      {...rest}
    >
      <FormProvider {...methods}>
        <CardWrapper>
          <RowBox columns={5}>
            <Box sx={{ gridColumn: isMobile ? 'span 1' : 'span 3' }}>
              <Input
                required
                name="profileDescription"
                label="Descrição do Perfil"
                disabled={isProfilesLoading || isAccessProfileByIdLoading || !permissions?.canEdit}
              />
            </Box>

            <Select
              required
              name="profileType"
              label="Tipo de Perfil"
              options={profileTypeOptions}
              disabled={isProfilesLoading || isAccessProfileByIdLoading || !permissions?.canEdit || !isSuperAdmin}
            />
            <Select
              required
              name="status"
              label="Status"
              options={statusOptions}
              disabled={isProfilesLoading || isAccessProfileByIdLoading || !permissions?.canEdit}
            />
          </RowBox>

          <RowBox>
            <Textarea
              label="Observações"
              name="observations"
              placeholder="Descreva as suas observações (opcional)"
              disabled={isProfilesLoading || isAccessProfileByIdLoading || !permissions?.canEdit}
            />
          </RowBox>
        </CardWrapper>

        <Title label="Permissões" />

        <CardWrapper style={{ display: 'flex', flexDirection: 'column' }}>
          {isAccessProfileByIdLoading ? (
            <LoadingGrid />
          ) : (
            <>
              <Switch
                name="fullAccess"
                label="Permitir Acesso Total ao Usuário"
                onChange={(_, checked) => {
                  methods.setValue('fullAccess', checked)
                }}
                disabled={!permissions?.canEdit}
              />

              <PermissionSettings data={data} />
            </>
          )}
        </CardWrapper>
      </FormProvider>
    </BaseModal2>
  )
}
